import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, X, Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const PROMO_CODE = "EARLYBIRD20";
const PROMO_DISCOUNT = 20;
const PROMO_DISMISS_KEY = "promo_dismissed_v1";

/** Site-wide banner shown ONLY when user is on trial with ≤3 days remaining. */
export function TrialCountdownBanner() {
  const { user } = useAuth();
  const sub = user?.subscription;
  if (!sub || sub.status !== "trial") return null;
  const days = sub.days_left ?? 0;
  if (days > 3) return null;

  const urgent = days <= 1;
  const label = days === 0
    ? "Your trial ends today"
    : days === 1
      ? "Last day of your trial"
      : `${days} days left on your trial`;

  return (
    <div
      data-testid="trial-countdown-banner"
      className={`w-full text-center text-sm flex items-center justify-center gap-3 px-4 py-2 ${urgent ? "bg-red-600 text-white" : "bg-amber-500 text-slate-950"}`}
    >
      <Clock className="w-4 h-4 flex-none" />
      <span className="font-medium">{label}</span>
      <span className="opacity-80 hidden sm:inline">— keep your full Pro access without interruption.</span>
      <Button
        asChild
        size="sm"
        data-testid="trial-banner-upgrade"
        className={`h-7 px-3 ${urgent ? "bg-white text-red-700 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-800"}`}
      >
        <Link to="/pricing">Upgrade now</Link>
      </Button>
    </div>
  );
}

/** One-time popup at day 5 of trial (≤2 days into trial = high engagement window),
 * offering a 20% off coupon. Dismissed permanently via localStorage. */
export function TrialPromoPopup() {
  const { user } = useAuth();
  const sub = user?.subscription;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!sub || sub.status !== "trial") return;
    // Day-5 of trial = 7 days minus 2 = days_left === 2
    if (sub.days_left !== 2) return;
    if (localStorage.getItem(PROMO_DISMISS_KEY)) return;
    const timer = setTimeout(() => setOpen(true), 1200); // small delay so it doesn't feel jarring
    return () => clearTimeout(timer);
  }, [sub]);

  const close = (markDismissed) => {
    setOpen(false);
    if (markDismissed) localStorage.setItem(PROMO_DISMISS_KEY, new Date().toISOString());
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(PROMO_CODE);
  };

  if (!open) return null;

  // When the user clicks "Claim 20% off", we route to /pricing with the promo
  // pre-stored in sessionStorage. The Pricing page picks it up and applies it on
  // the next /billing/subscribe call so the user lands on Stripe checkout with the
  // discount already applied — no manual paste needed.
  const claim = () => {
    sessionStorage.setItem("pending_promo_code", PROMO_CODE);
    close(true);
  };

  return (
    <div
      data-testid="trial-promo-popup"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-950/40 backdrop-blur-sm p-4"
      onClick={() => close(true)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        <button
          onClick={() => close(true)}
          data-testid="promo-close"
          className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/80 hover:bg-slate-100 flex items-center justify-center text-slate-500"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white px-7 py-8">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/20 mb-4">
            <Sparkles className="w-3 h-3" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em]">Trial offer</span>
          </div>
          <h3 className="font-outfit text-3xl font-bold leading-tight">Save {PROMO_DISCOUNT}% on your first year.</h3>
          <p className="mt-2 text-emerald-50 text-sm">
            We&apos;d love to keep you around. Use this code at checkout — it&apos;s only available while your trial is active.
          </p>
        </div>
        <div className="px-7 py-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">Promo code</div>
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg">
            <code className="font-mono text-xl font-bold tracking-widest text-slate-900" data-testid="promo-code">{PROMO_CODE}</code>
            <Button
              size="sm"
              variant="ghost"
              onClick={copyCode}
              data-testid="promo-copy"
              className="text-slate-700 hover:bg-slate-200"
            >
              Copy
            </Button>
          </div>
          <Badge variant="outline" className="mt-3 text-[10px] font-mono">Auto-applied at Stripe checkout · Expires when your trial ends</Badge>
          <div className="mt-6 flex gap-2">
            <Button
              asChild
              data-testid="promo-cta-pricing"
              size="lg"
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white h-11"
              onClick={claim}
            >
              <Link to="/pricing">
                <Gift className="w-4 h-4 mr-2" />
                Claim {PROMO_DISCOUNT}% off
              </Link>
            </Button>
            <Button
              variant="ghost"
              onClick={() => close(true)}
              data-testid="promo-dismiss"
              className="h-11 text-slate-500 hover:text-slate-700"
            >
              Maybe later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
